import React from 'react';

import FolderLayout from 'Components/HumanResources/StaffFolder/FolderLayout';
import {Card, SmallLink, SmallButton, Pill, Stat, Details, SimpleTable, plural} from 'Components/HumanResources/StaffFolder/Ui';
import {Loading} from 'Components/Partials';
import CreateTimesheetModal from 'Components/HumanResources/Timesheets/CreateTimesheetModal';
import ApproveHoliday from 'Components/HumanResources/Timesheets/ApproveHoliday';
import ShowTimesheetModal from 'Components/HumanResources/Timesheets/ShowTimesheetModal';

import {Modal, Toast} from 'Services';
import UsersApi from 'Services/Api/Admin/HumanResources/Users';
import UserSettingsApi from 'Services/Api/Admin/HumanResources/UserSettings';
import AdminTimesheetsApi from 'Services/Api/Admin/HumanResources/Timesheets';

const STATUS = {
    pending: ['Awaiting approval', 'warn'],
    approved: ['Approved', 'good'],
    declined: ['Declined', 'bad'],
};

/**
 * Staff folder: Holiday. Allowance and balance for a holiday year, leave requests to approve,
 * sickness, and the person's leave settings.
 */
export default class Holiday extends React.Component {
    /**
     * @var state
     */
    state = {
        year: null,
        holiday: null,
        settings: null,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.load();

        AdminTimesheetsApi.on('updated', this.load);
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        AdminTimesheetsApi.removeListener('updated', this.load);
    };

    /**
     * @method load
     */
    load = async () => {
        const userId = this.props.match.params.user;

        const [holiday, settings] = await Promise.all([
            UsersApi.getFolderHoliday(userId, this.state.year),
            UserSettingsApi.get({user: userId}),
        ]);

        if (!holiday.success) {
            return Toast.error();
        }

        this.setState({
            holiday: holiday.data.data,
            year: holiday.data.data.year,
            settings: settings.success ? settings.data.data : null,
        });
    };

    /**
     * @method changeYear
     * @param {Event} event
     */
    changeYear = (event) => {
        this.setState({year: Number(event.target.value), holiday: null}, this.load);
    };

    /**
     * @method add
     * @param {string} type holiday or sick
     */
    add = (type) => {
        Modal.open({
            component: CreateTimesheetModal,
            props: {type, admin: true, employee_id: this.props.match.params.user},
        });
    };

    /**
     * @method approve
     * @param {object} leave
     */
    approve = async (leave) => {
        const response = await AdminTimesheetsApi.get(leave.id);

        if (!response.success) {
            return Toast.error();
        }

        Modal.open({component: ApproveHoliday, props: {timesheet: response.data.data}});
    };

    /**
     * @method decline
     * @param {object} leave
     */
    decline = async (leave) => {
        const response = await AdminTimesheetsApi.get(leave.id);

        if (!response.success) {
            return Toast.error();
        }

        const update = await AdminTimesheetsApi.patch({record: leave.id}, {...response.data.data, rejected: true});

        update.success ? Toast.success('Holiday request declined.') : Toast.error();
    };

    /**
     * @method view
     * @param {object} entry
     */
    view = (entry) => {
        Modal.open({component: ShowTimesheetModal, props: {timesheet_id: entry.id, admin: true}});
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const userId = this.props.match.params.user;
        const {holiday, settings} = this.state;
        const yesNo = (value) => value ? 'Yes' : 'No';

        return (
            <FolderLayout userId={userId}>
                {() => !holiday ? <Loading/> : (
                    <div className="space-y-5">
                        <div className="flex flex-wrap items-end justify-between gap-4">
                            <div className="flex flex-col gap-1">
                                <label htmlFor="hol-year" className="text-sm font-bold text-gray-600">Holiday year</label>
                                <select
                                    id="hol-year"
                                    className="rounded-xl border-2 border-gray-300 py-2 px-3 bg-white"
                                    value={holiday.year}
                                    onChange={this.changeYear}
                                >
                                    {holiday.years.map(year => (
                                        <option key={year.value} value={year.value}>{year.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <SmallButton onClick={() => this.add('sick')}>Record sickness</SmallButton>
                                <SmallButton primary onClick={() => this.add('holiday')}>Add annual leave</SmallButton>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <Stat
                                label="Allowance"
                                value={plural(holiday.allowance, 'day')}
                                note={holiday.hours_per_day ? `A day of leave is ${holiday.hours_per_day} hours` : null}
                            />
                            <Stat label="Taken" value={plural(holiday.taken, 'day')} note={`${holiday.taken_hours} hours`} />
                            <Stat
                                label="Awaiting approval"
                                value={plural(holiday.pending, 'day')}
                                note={plural(holiday.pending_requests, 'request')}
                                tone={holiday.pending_requests ? 'warn' : null}
                            />
                            <Stat
                                label="Remaining"
                                value={plural(holiday.remaining, 'day')}
                                note={holiday.pending > 0 ? `${holiday.remaining_if_approved} if the pending requests are approved` : null}
                            />
                        </div>

                        <Card title="Annual leave" id="h-leave" subtitle={holiday.year_label}>
                            <SimpleTable
                                headings={['Dates', 'Days', 'Hours', 'Status', 'Requested', '']}
                                empty="No annual leave in this holiday year."
                                rows={holiday.leave.map(leave => [
                                    <span className="font-bold">{leave.dates}</span>,
                                    leave.days,
                                    leave.hours,
                                    <Pill tone={STATUS[leave.status][1]}>{STATUS[leave.status][0]}</Pill>,
                                    leave.requested,
                                    <div className="flex justify-end gap-2">
                                        {leave.status === 'pending' ? (
                                            <>
                                                <SmallButton onClick={() => this.decline(leave)}>Decline</SmallButton>
                                                <SmallButton primary onClick={() => this.approve(leave)}>Approve</SmallButton>
                                            </>
                                        ) : (
                                            <button type="button" className="font-bold text-app-leading hover:underline" onClick={() => this.view(leave)}>
                                                View
                                            </button>
                                        )}
                                    </div>,
                                ])}
                            />
                        </Card>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            <Card
                                title="Sickness"
                                id="h-sick"
                                subtitle={`${plural(holiday.sick_days, 'day')} this holiday year` +
                                    (holiday.sick_days_allowed ? ` (allowance ${holiday.sick_days_allowed})` : '')}
                            >
                                <SimpleTable
                                    headings={['Date', 'Days', '']}
                                    empty="No sickness recorded."
                                    rows={holiday.sickness.map(entry => [
                                        entry.dates,
                                        plural(entry.days, 'day'),
                                        <button type="button" className="font-bold text-app-leading hover:underline" onClick={() => this.view(entry)}>
                                            View
                                        </button>,
                                    ])}
                                />
                            </Card>

                            <Card
                                title="Leave settings"
                                id="h-set"
                                action={<SmallLink to={`/admin/human-resources/settings/${userId}`}>Edit</SmallLink>}
                            >
                                {settings ? (
                                    <Details items={[
                                        ['Annual holiday allowance', plural(settings.holiday_allowed, 'day')],
                                        ['Annual sickness allowance', plural(settings.sick_days_allowed, 'day')],
                                        ['Days per week', settings.days_per_week],
                                        ['Hours per day', settings.hours_per_day],
                                        ['Annual leave needs approval', yesNo(settings.holiday_needs_approval)],
                                        ['Sickness needs approval', yesNo(settings.sick_days_needs_approval)],
                                        ['Ignore UK bank holidays', yesNo(settings.ignore_bank_holidays)],
                                    ]}/>
                                ) : <p className="text-gray-600">No leave settings yet.</p>}
                            </Card>
                        </div>
                    </div>
                )}
            </FolderLayout>
        );
    }
}
